/**
 * Application Configuration
 *
 * Centralized configuration from environment variables.
 * All environment variables are prefixed with VITE_ for Vite to expose them.
 *
 * @module shared/config
 */

// ============================================================================
// Environment Types
// ============================================================================

export type AppEnvironment = 'development' | 'staging' | 'production';

// ============================================================================
// Environment Variable Access (using bracket notation for TypeScript)
// ============================================================================

const env = import.meta.env;

const VITE_APP_ENV = env['VITE_APP_ENV'] as AppEnvironment | undefined;
const VITE_BASE_API_URL = env['VITE_BASE_API_URL'] as string | undefined;
const VITE_BASE_SOCKETIO_URL = env['VITE_BASE_SOCKETIO_URL'] as string | undefined;
const VITE_WEBRTC_REALM = env['VITE_WEBRTC_REALM'] as string | undefined;
const VITE_WEBRTC_REGISTRAR_SERVER = env['VITE_WEBRTC_REGISTRAR_SERVER'] as string | undefined;
const VITE_WEBRTC_WEBSOCKET_URL = env['VITE_WEBRTC_WEBSOCKET_URL'] as string | undefined;
const VITE_SYMMETRIC_ENCRYPTION_KEY = env['VITE_SYMMETRIC_ENCRYPTION_KEY'] as string | undefined;

// ============================================================================
// Configuration Object
// ============================================================================

export const config = {
  /**
   * Application Environment
   */
  env: VITE_APP_ENV || 'development',

  /**
   * Check if running in development mode
   */
  isDevelopment: VITE_APP_ENV === 'development' || env.DEV,

  /**
   * Check if running in production mode
   */
  isProduction: VITE_APP_ENV === 'production' || env.PROD,

  /**
   * API Configuration
   */
  api: {
    /**
     * Backend API Base URL
     */
    baseUrl: VITE_BASE_API_URL || 'http://localhost:5001',

    /**
     * API Request Timeout (in milliseconds)
     */
    timeout: 30000,
  },

  /**
   * Socket.IO Configuration
   */
  socket: (() => {
    const raw = VITE_BASE_SOCKETIO_URL || VITE_BASE_API_URL || 'http://localhost:5001';
    const parsed = new URL(raw);
    const host = parsed.origin;
    const path = parsed.pathname === '/' ? '/socket.io' : parsed.pathname;
    return { url: raw, host, path };
  })(),

  /**
   * WebRTC / SIP (SIP.js) Configuration
   */
  webrtc: {
    realm: VITE_WEBRTC_REALM ?? '',
    registrarServer: VITE_WEBRTC_REGISTRAR_SERVER ?? '',
    websocketUrl: VITE_WEBRTC_WEBSOCKET_URL ?? '',
    symmetricEncryptionKey: VITE_SYMMETRIC_ENCRYPTION_KEY ?? '',
  },
} as const;

// ============================================================================
// Type Exports
// ============================================================================

export type Config = typeof config;

// ============================================================================
// Debug Logging (Development Only)
// ============================================================================

if (config.isDevelopment) {
  console.log('[Config] Environment:', config.env);
  console.log('[Config] API Base URL:', config.api.baseUrl);
  console.log('[Config] Socket URL:', config.socket.url);
  console.log('[Config][WebRTC] realm:', config.webrtc.realm);
  console.log('[Config][WebRTC] registrarServer:', config.webrtc.registrarServer);
  console.log('[Config][WebRTC] websocketUrl:', config.webrtc.websocketUrl);
}
